import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getConfirmationDraft, requestConfirmation } from '../services/api';
import useDialogFocus from '../hooks/useDialogFocus';
import { apiErrorMessage } from '../utils/apiError';
import { formatDate, formatDateTime, formatShortDate } from '../utils/dates';
import './ConfirmationRequestDialog.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Revoked requests are ignored everywhere in the UI: a revoked row is one the
// expert replaced, and the current row carries the state that matters.
export const activeConfirmations = (appt) => (appt?.confirmations || []).filter((c) => !c.revoked_at);

/** "Resend" once any request is live; old appointments only have the timestamp. */
export const hasSentRequest = (appt) => activeConfirmations(appt).length > 0;

const shortDate = formatShortDate;
// Interpolated dates contain "/", which i18next would HTML-escape.
const RAW = { interpolation: { escapeValue: false } };
const longDate = formatDate;

/**
 * Compact status badges for an appointment's confirmation requests, with a
 * full sentence in `title`. Falls back to the old "sent" hint for appointments
 * created before requests were tracked per party.
 */
export function ConfirmationBadges({ appt }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const list = activeConfirmations(appt);

  if (!list.length) {
    if (!appt.confirmation_sent_at) return null;
    return (
      <span className="confirm-badges">
        <span
          className="confirm-badge confirm-badge-confirmed"
          title={`${t('appointments.confirmationSent')} — ${formatDateTime(appt.confirmation_sent_at, lang)}`}
        >
          ✉
        </span>
      </span>
    );
  }

  return (
    <span className="confirm-badges">
      {list.map((c) => {
        const role = t(`confirmRequest.roleShort_${c.role}`);
        const out = [];
        if (c.confirmed_at) {
          out.push(
            <span
              key={`${c.id}-c`}
              className="confirm-badge confirm-badge-confirmed"
              title={t('confirmRequest.title_confirmed', { ...RAW, role, date: longDate(c.confirmed_at, lang) })}
            >
              {t('confirmRequest.badge_confirmed', { ...RAW, role, date: shortDate(c.confirmed_at, lang) })}
            </span>,
          );
        } else {
          out.push(
            <span
              key={`${c.id}-p`}
              className="confirm-badge confirm-badge-pending"
              title={t('confirmRequest.title_pending', { ...RAW, role, sent: longDate(c.sent_at, lang) })}
            >
              {t('confirmRequest.badge_pending', { role })}
            </span>,
          );
        }
        if (c.terms_accepted_at) {
          const date = longDate(c.terms_accepted_at, lang);
          out.push(
            <span
              key={`${c.id}-t`}
              className="confirm-badge confirm-badge-terms"
              title={c.terms_version
                ? t('confirmRequest.title_terms', { ...RAW, role, version: c.terms_version, date })
                : t('confirmRequest.title_termsNoVersion', { ...RAW, role, date })}
            >
              {t('confirmRequest.badge_terms', { ...RAW, date: shortDate(c.terms_accepted_at, lang) })}
            </span>,
          );
        }
        return out;
      })}
    </span>
  );
}

/**
 * Ask the client(s) of an appointment to confirm it. One row per party; the
 * expert decides per send who receives it and who must accept the terms — the
 * server only proposes the default (terms for the billing party).
 */
export default function ConfirmationRequestDialog({ appointment, onSent, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [draft, setDraft] = useState(null);
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const firstRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getConfirmationDraft(appointment.id)
      .then((d) => {
        if (!alive) return;
        setDraft(d);
        setNote(d.client_note || '');
        setRows(d.recipients.map((r) => ({
          role: r.role, name: r.name || '', email: r.email || '', send: true, terms: !!r.terms_required,
        })));
      })
      .catch((err) => { if (alive) setError(apiErrorMessage(err, t) || t('confirmRequest.loadFailed')); });
    return () => { alive = false; };
    // `t` only changes with the language: refetching then is harmless, and the
    // draft must not be refetched on other renders.
  }, [appointment.id, t]);

  const dialogRef = useRef(null);
  // The container takes focus right away; the first field once the data is in.
  useDialogFocus(dialogRef, { onEscape: () => { if (!busy) onClose(); } });

  // Focus the first field once the rows exist (they arrive after the fetch).
  useEffect(() => { if (draft) firstRef.current?.focus(); }, [draft]);

  const update = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const selected = rows.filter((r) => r.send);
  const termsUnavailable = draft && !draft.terms?.available;
  const askingTerms = selected.some((r) => r.terms);
  const enabled = draft?.enabled !== false;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selected.length) { setError(t('confirmRequest.nothingChosen')); return; }
    const bad = selected.filter((r) => !EMAIL_RE.test(r.email.trim()));
    if (bad.length) {
      setError(t('confirmRequest.badEmail', { list: bad.map((r) => r.email.trim() || t(`confirmRequest.role_${r.role}`)).join(', '), interpolation: { escapeValue: false } }));
      return;
    }
    setBusy(true);
    try {
      const updated = await requestConfirmation(appointment.id, selected.map((r) => ({
        role: r.role, email: r.email.trim(), name: r.name || undefined, terms_required: r.terms,
      })), note.trim());
      onSent(updated, selected.map((r) => r.email.trim()));
    } catch (err) {
      // 502 carries "L'e-mail n'est pas parti : …" — shown as is.
      setError(apiErrorMessage(err, t));
      setBusy(false);
    }
  };

  const resend = draft ? draft.confirmations?.length > 0 : hasSentRequest(appointment);

  return (
    <div className="confirm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form
        ref={dialogRef}
        tabIndex={-1}
        className="confirm-dialog send-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-request-title"
        onSubmit={submit}
      >
        <h2 id="confirm-request-title" className="confirm-title">{t('confirmRequest.title')}</h2>
        <p className="send-dialog-intro">
          {t('confirmRequest.intro', { title: appointment.title, interpolation: { escapeValue: false } })}
        </p>

        {!draft && !error && <p>{t('confirmRequest.loading')}</p>}

        {draft && !enabled && (
          <div className="send-warning confirm-warning" role="alert"><p>{t('confirmRequest.disabled')}</p></div>
        )}

        {draft && rows.map((r, i) => {
          const prior = (draft.confirmations || []).filter((c) => c.role === r.role);
          const role = t(`confirmRequest.role_${r.role}`);
          const mailId = `confirm-email-${r.role}`;
          return (
            <fieldset key={r.role} className={`confirm-party ${r.send ? '' : 'is-off'}`}>
              <legend className="visually-hidden">{role}</legend>
              <div className="confirm-party-head">
                <span className="confirm-party-role">{role}</span>
                {r.name && <span className="confirm-party-name">{r.name}</span>}
              </div>
              <label htmlFor={mailId} className="visually-hidden">{`${role} — ${t('confirmRequest.email')}`}</label>
              <input
                id={mailId}
                ref={i === 0 ? firstRef : null}
                className="form-control"
                type="text"
                inputMode="email"
                autoComplete="email"
                placeholder={t('confirmRequest.email')}
                value={r.email}
                onChange={(e) => update(i, { email: e.target.value })}
                disabled={!r.send}
              />
              {!r.email && <p className="send-hint">{t('confirmRequest.noEmail')}</p>}
              <div className="confirm-party-checks">
                <label className="send-check">
                  <input
                    type="checkbox"
                    checked={r.send}
                    aria-label={`${t('confirmRequest.send')} — ${role}`}
                    onChange={(e) => update(i, { send: e.target.checked })}
                  />
                  {t('confirmRequest.send')}
                </label>
                <label className="send-check">
                  <input
                    type="checkbox"
                    checked={r.terms}
                    disabled={!r.send}
                    aria-label={`${t('confirmRequest.termsRequired')} — ${role}`}
                    onChange={(e) => update(i, { terms: e.target.checked })}
                  />
                  {t('confirmRequest.termsRequired')}
                </label>
              </div>
              {prior.length > 0 && (
                <ul className="confirm-party-history">
                  {prior.map((c) => (
                    <li key={c.id}>
                      {c.sent_count > 1
                        ? t('confirmRequest.sentOn_times', { ...RAW, date: longDate(c.sent_at, lang), count: c.sent_count })
                        : t('confirmRequest.sentOn', { ...RAW, date: longDate(c.sent_at, lang) })}
                      {c.confirmed_at && ` · ${t('confirmRequest.confirmedOn', { ...RAW, date: longDate(c.confirmed_at, lang) })}`}
                      {c.terms_accepted_at && ` · ${t('confirmRequest.termsOn', { ...RAW, date: longDate(c.terms_accepted_at, lang) })}`}
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          );
        })}

        {draft && (
          <div className="form-group">
            <label htmlFor="confirm-client-note">{t('confirmRequest.noteLabel')}</label>
            <textarea
              id="confirm-client-note"
              className="form-control"
              rows={3}
              maxLength={2000}
              value={note}
              placeholder={t('confirmRequest.notePlaceholder')}
              onChange={(e) => setNote(e.target.value)}
              aria-describedby="confirm-client-note-hint"
            />
            <p id="confirm-client-note-hint" className="send-hint">{t('confirmRequest.noteHint')}</p>
          </div>
        )}

        {draft && draft.terms?.available && (
          <p className="send-hint">
            {t('confirmRequest.termsInfo', {
              ...RAW,
              version: draft.terms.version, date: longDate(draft.terms.uploaded_at, lang),
            })}
          </p>
        )}

        {termsUnavailable && askingTerms && (
          <div className="send-warning confirm-warning" role="status">
            <p>{t('confirmRequest.noTerms')} {t('confirmRequest.noTermsAdmin')}</p>
          </div>
        )}

        {error && <div className="ai-error" role="alert">{error}</div>}

        <div className="confirm-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t('clients.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !draft || !enabled}>
            {busy ? t('confirmRequest.sending') : t(resend ? 'confirmRequest.submitResend' : 'confirmRequest.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
